import type { CustomerCreateInput, CustomerPatchInput } from '@today/models'
import { apiClient } from './api.client'

/**
 * Customer API client (TO-418). Shapes mirror the customer-service DTOs
 * (camelCase responses, snake_case request bodies).
 */

export interface ContactValue {
	contactPointId: string
	value: string
	masked: boolean
}

export interface CustomerDTO {
	customerId: string
	externalId: string | null
	firstName: string
	lastName1: string | null
	lastName2: string | null
	fullName: string
	type: 'lead' | 'customer'
	status: 'active' | 'inactive'
	statusPartnerLabel: string | null
	convertedAt: string | null
	preferredChannel: string | null
	preferredLocale: string | null
	tags: string[]
	orgUnit: { id: string; code: string; name: string }
	assignedUser: { id: string; displayName: string | null }
	primaryEmail: ContactValue | null
	primaryMobile: ContactValue | null
	source: 'file_import' | 'admin_ui' | 'api'
	anonymizedAt: string | null
	createdAt: string
	updatedAt: string
}

export interface ContactPointDTO {
	contactPointId: string
	type: 'email' | 'mobile' | 'landline'
	value: string
	label: 'personal' | 'work' | 'other'
	priority: number
	isPrimary: boolean
	verifiedAt: string | null
	invalidatedAt: string | null
	masked: boolean
}

export interface AssignmentDTO {
	assignmentId: string
	orgUnit: { id: string; code: string; name: string }
	assignedUser: { id: string; displayName: string | null }
	validFrom: string
	validTo: string | null
	reason: string
}

export interface NoteDTO {
	noteId: string
	body: string
	authoredBy: string
	authorDisplayName: string | null
	edited: boolean
	createdAt: string
}

export interface ConsentDTO {
	consentId: string
	channel: string
	purpose: string
	status: string
	legalTextId: string | null
	legalTextVersion: string | null
	evidence: Record<string, unknown> | null
	evidenceRedacted: boolean
	effectiveAt: string
}

export interface AttributeDTO {
	key: string
	value: unknown
	source: string
}

export interface ActivityDTO {
	eventId: string
	type: string
	timestamp: string
}

export interface Paged<T> {
	data: T[]
	total: number
	page: number
	limit: number
	totalPages: number
}

export interface LookupResult {
	data: Array<{ customerId: string; fullName: string; orgUnit: { id: string; name: string } }>
	outOfScopeMatches: number
}

export interface OrgUnitOption {
	id: string
	code: string
	name: string
	parentId: string | null
	status: string
}

export interface UserOption {
	userId: string
	displayName: string
}

const EMPTY_PAGE = <T>(page: number, limit: number): Paged<T> => ({ data: [], total: 0, page, limit, totalPages: 0 })

/**
 * A paginated GET. Lists now always answer `200 {data:[],total:0}` on empty
 * (TO-632, AD-21.7), so the body is the page; `EMPTY_PAGE` stays only as a
 * defensive fallback for a missing body.
 */
const getPage = async <T>(url: string, params: Record<string, string>): Promise<Paged<T>> => {
	const response = await apiClient.get<Paged<T> | ''>(url, { params })
	const page = Number(params.page ?? 1)
	const limit = Number(params.limit ?? 20)
	return response.data ? response.data : EMPTY_PAGE<T>(page, limit)
}

const base = (id: string) => `/customers/${encodeURIComponent(id)}`

export const customerService = {
	list: (params: Record<string, string>) => getPage<CustomerDTO>('/customers', params),
	get: async (id: string) => (await apiClient.get<CustomerDTO>(base(id))).data,
	create: async (body: CustomerCreateInput) => (await apiClient.post<CustomerDTO>('/customers', body)).data,
	update: async (id: string, body: CustomerPatchInput) => (await apiClient.patch<CustomerDTO>(base(id), body)).data,
	reassign: async (id: string, body: { org_unit_id: string; assigned_user_id: string; reason: string }) =>
		(await apiClient.post<CustomerDTO>(`${base(id)}/assignment`, body)).data,
	anonymize: async (id: string) => (await apiClient.post<CustomerDTO>(`${base(id)}/anonymize`, {})).data,
	lookup: async (type: string, value: string) =>
		(await apiClient.get<LookupResult>('/customers/lookup', { params: { type, value } })).data,
	contactPoints: async (id: string) =>
		(await apiClient.get<{ data: ContactPointDTO[] }>(`${base(id)}/contact-points`)).data.data,
	addContactPoint: async (id: string, body: { type: string; value: string }) =>
		(await apiClient.post<ContactPointDTO>(`${base(id)}/contact-points`, body)).data,
	updateContactPoint: async (id: string, contactPointId: string, body: Record<string, unknown>) =>
		(await apiClient.patch<ContactPointDTO>(`${base(id)}/contact-points/${contactPointId}`, body)).data,
	deleteContactPoint: async (id: string, contactPointId: string) => {
		await apiClient.delete(`${base(id)}/contact-points/${contactPointId}`)
	},
	reorderContactPoints: async (id: string, type: string, contactPointIds: string[]) => {
		await apiClient.put(`${base(id)}/contact-points/order`, { type, contact_point_ids: contactPointIds })
	},
	assignments: async (id: string) =>
		(await apiClient.get<{ data: AssignmentDTO[] }>(`${base(id)}/assignments`)).data.data,
	notes: async (id: string) => (await getPage<NoteDTO>(`${base(id)}/notes`, { page: '1', limit: '100' })).data,
	addNote: async (id: string, body: string) => (await apiClient.post<NoteDTO>(`${base(id)}/notes`, { body })).data,
	updateNote: async (id: string, noteId: string, body: string) =>
		(await apiClient.patch<NoteDTO>(`${base(id)}/notes/${noteId}`, { body })).data,
	deleteNote: async (id: string, noteId: string) => {
		await apiClient.delete(`${base(id)}/notes/${noteId}`)
	},
	consents: async (id: string) =>
		(await apiClient.get<{ current: ConsentDTO[]; history: ConsentDTO[] }>(`${base(id)}/consents`)).data,
	recordConsent: async (id: string, body: { channel: string; purpose: string; status: string }) =>
		(await apiClient.post<ConsentDTO>(`${base(id)}/consents`, body)).data,
	attributes: async (id: string) => (await apiClient.get<{ data: AttributeDTO[] }>(`${base(id)}/attributes`)).data.data,
	activity: async (id: string) =>
		(
			await getPage<ActivityDTO>('/events', {
				'filters[entityType]': 'customer',
				'filters[entityId]': id,
				page: '1',
				limit: '100',
			})
		).data,
	/** Every ACTIVE org unit (all pages) for the hierarchical picker. */
	orgUnits: async (): Promise<OrgUnitOption[]> => {
		const all: OrgUnitOption[] = []
		for (let page = 1; ; page++) {
			const result = await getPage<OrgUnitOption>('/organization/units', { page: String(page), limit: '100' })
			all.push(...result.data)
			if (page >= result.totalPages) break
		}
		return all.filter((unit) => unit.status === 'ACTIVE')
	},
	/** The tenant's users for the assignee picker, by the viewer's session slug (TO-628). */
	users: async (tenantSlug: string): Promise<UserOption[]> => {
		const result = await getPage<UserOption>(`/t/${encodeURIComponent(tenantSlug)}/admin/users`, {
			page: '1',
			limit: '100',
		})
		return result.data
	},
}
